class InPageNavigation extends HTMLElement {
  constructor () {
    super()
    this.sections = this.populateSections()
    this.isScrolling = false
    this.targetScrollY = null
    this.addButtonClickListeners()
    this.addScrollListener()
    this.addListScrollListener()
  }

  populateSections () {
    const sections = []
    this.buttons.forEach((button) => {
      const sectionId = button.dataset.link
      if (sectionId) {
        const section = document.querySelector(`#${sectionId}`)
        if (section) {
          sections.push(section)
        }
      }
    })
    return sections
  }

  addButtonClickListeners () {
    this.buttons.forEach((button) => {
      button.addEventListener('click', () => {
        const sectionId = button.dataset.link
        const section = this.sections.find((section) => section.id === sectionId)
        if (section) {
          this.isScrolling = true
          const targetScrollY = window.scrollY + section.getBoundingClientRect().top - this.navOffset

          window.scrollTo({
            top: targetScrollY,
            behavior: 'smooth',
          })
          this.updateButtonClasses(sectionId)
          this.targetScrollY = Math.round(targetScrollY)

          this.scrollButtonIntoView(button)
        }
      })
    })
  }

  scrollButtonIntoView (button) {
    const container = this.listContainer
    const buttonLeft = button.offsetLeft
    const buttonWidth = button.offsetWidth
    const containerWidth = container.clientWidth

    // Calculate the new scroll position to center the button
    let newScrollLeft = buttonLeft - containerWidth / 2 + buttonWidth / 2

    // Ensure the new scroll position is within bounds
    newScrollLeft = Math.max(0, Math.min(newScrollLeft, container.scrollWidth - containerWidth))

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    })
  }

  addScrollListener () {
    window.addEventListener('scroll', () => this.onScroll(), { passive: true })
    this.onScroll()
  }

  addListScrollListener () {
    this.listContainer.addEventListener('scroll', () => this.updateFaders(), { passive: true })
    window.addEventListener('resize', () => this.updateFaders())
    this.updateFaders()

    this.previousButton.addEventListener('click', () => this.scrollByButton(-1))
    this.nextButton.addEventListener('click', () => this.scrollByButton(1))
  }

  scrollByButton (direction) {
    const container = this.listContainer
    const buttonWidth = this.buttons[0].offsetWidth
    const offset = buttonWidth * direction

    container.scrollBy({
      left: offset,
      behavior: 'smooth',
    })
  }

  onScroll () {
    if (this.isScrolling) {
      const currentScrollY = Math.round(window.scrollY)
      if (Math.abs(currentScrollY - this.targetScrollY) < 2) {
        this.isScrolling = false
        this.targetScrollY = null
      } else {
        return
      }
    }

    const viewportHeight = window.innerHeight

    let activeSectionId = null
    let minDistance = Infinity

    this.sections.forEach((section) => {
      const rect = section.getBoundingClientRect()
      const adjustedTop = rect.top - this.navOffset
      const distance = Math.abs(adjustedTop)
      if (adjustedTop <= viewportHeight && distance < minDistance) {
        minDistance = distance
        activeSectionId = section.id
      }
    })

    if (activeSectionId) {
      this.updateButtonClasses(activeSectionId)

      clearTimeout(this.centeringTimeout)

      this.centeringTimeout = setTimeout(() => {
        const activeButton = Array.from(this.buttons).find(
          (button) => button.dataset.link === activeSectionId,
        )
        if (activeButton && this.listContainer.scrollWidth > this.listContainer.clientWidth) {
          this.scrollButtonIntoView(activeButton)
        }
      }, 100)
    }
  }

  updateButtonClasses (activeSectionId) {
    const defaultBg = this.dataset.defaultBg
    const defaultText = this.dataset.defaultText
    const defaultBorder = this.dataset.defaultBorder
    const activeBg = this.dataset.activeBg
    const activeText = this.dataset.activeText
    const activeBorder = this.dataset.activeBorder

    this.buttons.forEach((button) => {
      const sectionId = button.dataset.link
      const isActive = sectionId === activeSectionId

      if (isActive) {
        button.classList.add('is-active')
        if (defaultBg) button.classList.remove(defaultBg)
        if (defaultText) button.classList.remove(defaultText)
        if (defaultBorder) button.classList.remove(defaultBorder)
        if (activeBg) button.classList.add(activeBg)
        if (activeText) button.classList.add(activeText)
        if (activeBorder) button.classList.add(activeBorder)
      } else {
        button.classList.remove('is-active')
        if (activeBg) button.classList.remove(activeBg)
        if (activeText) button.classList.remove(activeText)
        if (activeBorder) button.classList.remove(activeBorder)
        if (defaultBg) button.classList.add(defaultBg)
        if (defaultText) button.classList.add(defaultText)
        if (defaultBorder) button.classList.add(defaultBorder)
      }
    })
  }

  updateFaders () {
    const container = this.listContainer
    const isScrollable = container.scrollWidth > container.clientWidth

    if (!isScrollable) {
      this.faders.classList.add('hidden')
      this.controls.classList.add('hidden')
      this.previousButton.classList.add('hidden')
      this.nextButton.classList.add('hidden')
      return
    } else {
      this.faders.classList.remove('hidden')
      this.controls.classList.remove('hidden')
      this.previousButton.classList.remove('hidden')
      this.nextButton.classList.remove('hidden')
    }

    const scrollLeft = container.scrollLeft
    const clientWidth = container.clientWidth
    const scrollWidth = container.scrollWidth

    const atStart = scrollLeft <= 0
    const atEnd = scrollLeft + clientWidth >= scrollWidth - 1

    this.previousButton.classList.toggle('hidden', atStart)
    this.nextButton.classList.toggle('hidden', atEnd)

    this.faderLeft.classList.toggle('opacity-0', atStart)
    this.faderLeft.classList.toggle('opacity-100', !atStart)

    this.faderRight.classList.toggle('opacity-0', atEnd)
    this.faderRight.classList.toggle('opacity-100', !atEnd)
  }

  get buttons () {
    return this.querySelectorAll('button[data-link]')
  }

  get listContainer () {
    return this.querySelector('.in-page-nav__list-container')
  }

  get faderLeft () {
    return this.querySelector('.in-page-nav__fader--left')
  }

  get faderRight () {
    return this.querySelector('.in-page-nav__fader--right')
  }

  get faders () {
    return this.querySelector('.in-page-nav__faders')
  }

  get controls () {
    return this.querySelector('.in-page-nav__controls')
  }

  get previousButton () {
    return this.querySelector('.in-page-nav__controls-button--previous')
  }

  get nextButton () {
    return this.querySelector('.in-page-nav__controls-button--next')
  }

  get displayType () {
    return this.dataset.displayType || 'pill'
  }

  get navContainer () {
    return this.closest('.section-tb-in-page-navigation') || this
  }

  get navOffset () {
    const container = this.navContainer
    const computedTop = parseFloat(window.getComputedStyle(container).top) || 0
    const containerHeight = container.getBoundingClientRect().height
    return computedTop + containerHeight
  }
}

if (!customElements.get('in-page-nav')) {
  customElements.define('in-page-nav', InPageNavigation)
}
